import { useQueryClient } from "@tanstack/react-query";
import { message, Row, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import RSelect from "react-select";
import { useGetMasterUsers } from "../../../../../api/v1";
import { SelectOption } from "../../../../../types/select";
import { findSelectOption, getSelectPlaceholderMessage, mapToSelectOptions } from "../../../../libs/form";
import Button from "../../../../molecules/Button";
import { ReassignRequest, reassignServiceRequest } from "../api";
import { ServiceRequestGrid } from "../types";

type ReassignUserSectionProps = {
  selectedRow: ServiceRequestGrid;
  onSuccess: () => void;
};

export const ReassignUserSection = (props: ReassignUserSectionProps) => {
  const { selectedRow, onSuccess } = props;

  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { masterUsers, isLoadingMasterUsers } = useGetMasterUsers({ isEnabled: true });

  const assignedUserOptions = useMemo(() => {
    return mapToSelectOptions(masterUsers, { labelKey: "name", valueKey: "userId" }) as SelectOption[];
  }, [masterUsers]);

  const [assignedUser, setAssignedUser] = useState<SelectOption>();

  useEffect(() => {
    if (assignedUserOptions.length === 0 || !selectedRow?.assignedUserId) return;

    const selectedUser = findSelectOption(assignedUserOptions, selectedRow.assignedUserId);

    if (!assignedUser?.value || assignedUser.value !== selectedUser?.value) {
      setAssignedUser(selectedUser);
    }
  }, [assignedUserOptions, selectedRow?.assignedUserId]);

  const handleReassignUser = async () => {
    try {
      setIsSubmitting(true);

      const payload: ReassignRequest = {
        ticketId: selectedRow?.ticketId,
        landlordId: assignedUser?.value,
      };
      await reassignServiceRequest(payload);
      message.success("User reassigned successfully");
      queryClient.invalidateQueries({ queryKey: ["service-requests"] });
      onSuccess?.();
    } catch (error) {
      message.error(error?.response?.data?.error ?? "Error occurred while reassigning user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Row>
        <Typography.Text>Assigned User</Typography.Text>
        <RSelect
          className="w-100"
          value={assignedUser}
          placeholder={getSelectPlaceholderMessage("assignedUser")}
          onChange={(selectedOption) => setAssignedUser(selectedOption)}
          options={assignedUserOptions}
          isLoading={isLoadingMasterUsers}
          isDisabled={isLoadingMasterUsers}
          styles={{
            control: (styles) => ({ ...styles, borderRadius: 6 }),
            valueContainer: (styles) => ({ ...styles, textTransform: "capitalize" }),
            dropdownIndicator: (styles) => ({ ...styles, padding: 5 }),
            indicatorSeparator: (styles) => ({ ...styles, backgroundColor: "#fff" }),
            option: (styles) => ({ ...styles, textTransform: "capitalize" }),
            placeholder: (styles) => ({ ...styles, color: "rgba(0,0,0,.3)" }),
            menuPortal: (styles) => ({ ...styles, zIndex: 99999999 }),
          }}
          theme={(styles) => ({ ...styles, borderRadius: 2, colors: { ...styles.colors, primary: "#767676" } })}
        />
      </Row>

      <Row style={{ display: "flex", justifyContent: "end", marginTop: "20px" }}>
        <Button
          style={{
            height: "2.25rem",
            width: "7rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          label={isSubmitting ? "Updating..." : "Update"}
          disabled={isSubmitting}
          onClick={handleReassignUser}
        />
      </Row>
    </div>
  );
};
